import {
  Badge,
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { MdChevronRight, MdOutlineNotificationsActive } from "react-icons/md";
import { Link } from "react-router-dom";
import { brandPalette } from "../../constants/brandPalette";
import { useStartsInLabel } from "../../hooks/useStartsInLabel";
import {
  buildRecentItems,
  RECENT_CONTENT_TYPE_META,
} from "../../utils/studentRecentContent";

function RecentBellItem({ item, isDark }) {
  const meta = RECENT_CONTENT_TYPE_META[item.type] || RECENT_CONTENT_TYPE_META.video;
  const Icon = meta.Icon;
  const dateLabel = item.date ? dayjs(item.date).format("D MMM") : "—";
  const startsIn = useStartsInLabel(item.startTime);

  return (
    <Flex
      as={Link}
      to={item.to}
      align="center"
      gap={3}
      px={3}
      py={3}
      borderBottomWidth="1px"
      borderColor="var(--border-subtle)"
      _hover={{
        bg: "rgba(91, 13, 22, 0.06)",
        textDecoration: "none",
      }}
      _last={{ borderBottomWidth: 0 }}
    >
      <Flex
        w="44px"
        h="44px"
        bg={meta.accent}
        color={brandPalette.cream}
        borderRadius="md"
        align="center"
        justify="center"
        direction="column"
        flexShrink={0}
        gap={0.5}
      >
        <Icon size={16} />
        <Text fontSize="8px" fontWeight="800" textTransform="uppercase" lineHeight="1">
          {dateLabel}
        </Text>
      </Flex>
      <Box minW={0} flex="1">
        <Text
          fontSize="9px"
          fontWeight="800"
          color="var(--text-muted)"
          textTransform="uppercase"
          letterSpacing="0.08em"
          mb={0.5}
        >
          {meta.label}
        </Text>
        <Text
          fontSize="sm"
          fontWeight="700"
          color={isDark ? "var(--text-primary)" : brandPalette.secondary}
          noOfLines={2}
        >
          {item.title}
        </Text>
        {startsIn ? (
          <Text
            fontSize="xs"
            fontWeight="800"
            color={brandPalette.primary}
            noOfLines={1}
            mt={0.5}
          >
            {startsIn}
          </Text>
        ) : null}
        <Text fontSize="xs" color="var(--text-muted)" noOfLines={1}>
          {item.subtitle}
        </Text>
      </Box>
      <Box as="span" display="inline-flex" color={brandPalette.primary} flexShrink={0}>
        <MdChevronRight size={20} />
      </Box>
    </Flex>
  );
}

/**
 * Sidebar / mobile bell that lists the same recent content as the home widget.
 */
const RecentContentBell = ({
  performanceData,
  isLoading = false,
  isDark = false,
  size = "md",
  placement = "top-end",
  ...buttonProps
}) => {
  const items = useMemo(() => buildRecentItems(performanceData), [performanceData]);
  const count = items.length;
  const iconSize = size === "sm" ? 18 : 20;

  return (
    <Menu placement={placement} isLazy>
      <MenuButton
        as={IconButton}
        aria-label="Recent content"
        icon={
          <Box position="relative" lineHeight={0}>
            <MdOutlineNotificationsActive size={iconSize} />
            {count > 0 ? (
              <Badge
                position="absolute"
                top="-2px"
                right="-2px"
                borderRadius="full"
                bg="red.500"
                minW="8px"
                w="8px"
                h="8px"
                p={0}
                pointerEvents="none"
              />
            ) : null}
          </Box>
        }
        variant="outline"
        borderColor={isDark ? brandPalette.cream : brandPalette.primary}
        color={brandPalette.primary}
        size={size}
        _hover={{
          bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(91, 13, 22, 0.08)",
        }}
        {...buttonProps}
      />

      <MenuList
        bg="var(--surface-raised)"
        borderWidth="1px"
        borderColor="var(--border-subtle)"
        borderRadius="lg"
        boxShadow="lg"
        py={0}
        minW={{ base: "min(100vw - 24px, 320px)", md: "340px" }}
        maxW="360px"
        zIndex={50}
        overflow="hidden"
      >
        <Box
          px={4}
          py={3}
          borderBottomWidth="1px"
          borderColor="var(--border-subtle)"
        >
          <Text
            fontSize="11px"
            fontWeight="800"
            letterSpacing="0.12em"
            textTransform="uppercase"
            color={isDark ? "var(--text-muted)" : brandPalette.secondary}
          >
            Recent content
          </Text>
        </Box>

        <Box
          maxH="360px"
          overflowY="auto"
          overflowX="hidden"
          className="scroll"
          css={{
            scrollbarWidth: "thin",
            scrollbarColor: "var(--scrollbar-thumb, #5B0D16) transparent",
          }}
        >
          {isLoading && count === 0 ? (
            <Flex align="center" justify="center" py={10}>
              <Spinner size="md" color={brandPalette.primary} thickness="3px" />
            </Flex>
          ) : count === 0 ? (
            <Text fontSize="sm" color="var(--text-muted)" px={4} py={6} lineHeight="tall">
              No recent videos, exams, PDFs, or homework yet.
            </Text>
          ) : (
            <VStack align="stretch" spacing={0}>
              {items.map((item) => (
                <RecentBellItem key={item.id} item={item} isDark={isDark} />
              ))}
            </VStack>
          )}
        </Box>
      </MenuList>
    </Menu>
  );
};

export default RecentContentBell;
